import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import api from "@/lib/axios"
import { System } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: System) => {
  const response = await api.post("/setting/sys-master/store", newData)
  return response.data
}

const patchData = async (newData: System) => {
  const response = await api.patch("/setting/sys-master/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_sys_master: string }) => {
  const response = await api({
    url: "/setting/sys-master/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: System | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<System>({
    id_sys_master: selectedRow?.id_sys_master || "",
    tahun_aktif: selectedRow?.tahun_aktif || "",
    sitename: selectedRow?.sitename || "",
    kop_kementrian: selectedRow?.kop_kementrian || "",
    kop_lembaga: selectedRow?.kop_lembaga || "",
    kop_lembaga_alamat: selectedRow?.kop_lembaga_alamat || "",
    iuran_kmhd: selectedRow?.iuran_kmhd || "",
    iuran_alhikmah: selectedRow?.iuran_alhikmah || "",
    iuran_parahyangan: selectedRow?.iuran_parahyangan || "",
    t_iuran_citradana: selectedRow?.t_iuran_citradana || "",
    id_ref_jalur_jenis_aktif: selectedRow?.id_ref_jalur_jenis_aktif || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  // const { data: dataJalurJenis } = useQuery({
  //   queryKey: ["listJalurJenis"],
  //   queryFn: getListJalurJenis,
  // });

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["masterSystem"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jalur"
          description={`Apakah anda yakin akan menghapus data jalur "${selectedRow?.id_sys_master}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data System" : "Update Data System"}
      />
      <div>
        <Label>Nama Situs</Label>
        <Input
          required
          placeholder="Ketik nama situs..."
          value={form.sitename}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, sitename: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Tahun Aktif</Label>
        <Input
          required
          placeholder="Ketik tahun aktif (contoh: 2024)..."
          value={form.tahun_aktif}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, tahun_aktif: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Kop Kementrian</Label>
        <Input
          required
          placeholder="Ketik kop kementrian..."
          value={form.kop_kementrian}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, kop_kementrian: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Kop Lembaga</Label>
        <Input
          required
          placeholder="Ketik kop lembaga..."
          value={form.kop_lembaga}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, kop_lembaga: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Alamat Kop Lembaga</Label>
        <Input
          required
          placeholder="Ketik alamat kop lembaga..."
          value={form.kop_lembaga_alamat}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kop_lembaga_alamat: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Iuran Parahyangan</Label>
        <Input
          placeholder="Ketik iuran Parahyangan..."
          value={form.iuran_parahyangan}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              iuran_parahyangan: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Iuran KMHD</Label>
        <Input
          placeholder="Ketik iuran KMHD..."
          value={form.iuran_kmhd}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              iuran_kmhd: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Iuran Alhikmah</Label>
        <Input
          placeholder="Ketik iuran Alhikmah..."
          value={form.iuran_alhikmah}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              iuran_alhikmah: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Iuran Citradana</Label>
        <Input
          placeholder="Ketik iuran Citradana..."
          value={form.t_iuran_citradana}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              t_iuran_citradana: event.target.value,
            }))
          }
        />
      </div>
      {/* <div>
        <Label>Jalur Jenis Aktif</Label>
        <Select
          value={form.id_ref_jalur_jenis_aktif}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jalur_jenis_aktif: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jalur_jenis_aktif &&
                dataJalurJenis?.find(
                  (jalurJenis: JalurJenis) =>
                    jalurJenis.id_ref_jalur_jenis ===
                    form.id_ref_jalur_jenis_aktif
                )?.nama_jalur_jenis}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJalurJenis?.map((jalurJenis: JalurJenis, index: number) => (
              <SelectItem
                key={index}
                value={`${jalurJenis.id_ref_jalur_jenis}`}
              >
                {jalurJenis?.nama_jalur_jenis}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div> */}

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
